/**
 * Hook: useIncomingGifs
 *
 * Real-time subscription to gifs still moving through the unified ingestion
 * pipeline — `pending_analysis`, `analyzing`, or `analysis_failed`. Powers the
 * "Incoming" section above the retrieval-review queue: the curator watches new
 * uploads analyze, and retries the ones that fail. Completed gifs leave this
 * list (they surface as unreviewed records in the review queue instead).
 */

import { useEffect, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import type { QueryDocumentSnapshot } from 'firebase/firestore'

import { db } from '../../../firebase'
import type { GifDocument } from '../../../types'

const INCOMING_STATUSES = [
  'pending_analysis',
  'analyzing',
  'analysis_failed',
] as const

export function useIncomingGifs() {
  const [incoming, setIncoming] = useState<GifDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // No orderBy: avoids a composite-index requirement and tolerates docs that
    // lack `uploadedAt`; we sort client-side below.
    const q = query(
      collection(db, 'gifs'),
      where('processingStatus', 'in', INCOMING_STATUSES)
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const gifs = snapshot.docs.map((doc: QueryDocumentSnapshot) => ({
          id: doc.id,
          ...doc.data(),
        })) as GifDocument[]
        // Failures first (they need action), then most recent uploads.
        gifs.sort((a, b) => {
          const aFailed = a.processingStatus === 'analysis_failed' ? 0 : 1
          const bFailed = b.processingStatus === 'analysis_failed' ? 0 : 1
          if (aFailed !== bFailed) return aFailed - bFailed
          const aTs = a.uploadedAt?.toMillis?.() ?? 0
          const bTs = b.uploadedAt?.toMillis?.() ?? 0
          return bTs - aTs
        })
        setIncoming(gifs)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Error fetching incoming gifs:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  return { incoming, loading, error, count: incoming.length }
}
